/**
 * VERA-790 — shared encode types + helpers, used across the encode modals (`EncodeTagsModal`,
 * `BulkEncodeTagsModal`), the reader hook (`useEncodeSession`), and the package/tags pages. Lives
 * on its own (was in the now-removed `EncodeConfigureModal`) so no modal owns the shared contract.
 */

/**
 * Match a handle tag's NDEF URL (`https://api.dev.vera.inc/pkg/<packageId>`) scanned off the
 * reader and capture the package id. The character class is a loose UUID shape (32 hex + 4 dashes
 * = 36 chars), matching the format `EncodePackageHandleModal` writes.
 */
export const PACKAGE_HANDLE_URL_RE = /\/pkg\/([0-9a-f-]{36})/i

/**
 * VRG-252 — the family union and everything known about each family now come from the tag-family
 * registry in `@repo/types`, which is also what the API and (mirrored) the reader read. This was a
 * fourth independent copy of the union, and the one that was missing `ntag213` while the catalog
 * was already selling Connect variants.
 */
export type { TagFamily, TagAssurance, TagFamilySpec } from '@repo/types'
export {
  getTagFamilySpec,
  familyAssurance,
  familyCarriesProof,
  isEncodableFamily,
} from '@repo/types'
import { ENCODABLE_TAG_FAMILIES, getTagFamilySpec, type TagFamily } from '@repo/types'

/**
 * The chip families the desk reader can encode today. DERIVED from each family's `encodeStrategy`
 * rather than hand-listed — the previous hand-listed constant said `['ntag424_dna']`, which is
 * what made every Connect variant unencodable and produced the dead end this task exists to close.
 *
 * A resolved family outside this set is a valid CATALOG chip (Shopify) whose reader-side write is
 * not built yet, so the encode blocks with "not supported on the desk reader yet".
 */
export const ENCODABLE_FAMILIES: readonly TagFamily[] = ENCODABLE_TAG_FAMILIES

/**
 * Whether locking this family means rotating keys (424) or setting permanent lock bits
 * (213 / EM near-field). The session needs to know, because the two have different consequences
 * and the toggle's copy must say which one the operator is agreeing to — a 424 lock is recoverable
 * only in the sense that the keys are stored; a Type-2 lock cannot be undone at all.
 */
export const familyLockMode = (family: TagFamily | null | undefined) =>
  family ? getTagFamilySpec(family).lockMode : null

/**
 * VERA-790 — the chip family for a SPECIFIC variant, taken ONLY from its explicit `tag_family`
 * (the Shopify variant metafield `custom.tag_family`). Returns null when unset — the encode is then
 * BLOCKED ("Missing tag type"), never guessed. Title/SKU inference was removed on purpose (the old
 * `familyForProduct`): a wrong family breaks encode + verification, so a blocked encode beats a
 * guessed one. See the `tag-family-enforce-metafield` decision.
 */
export function familyForVariant(
  variant?: { tag_family?: TagFamily | null } | null,
): TagFamily | null {
  return variant?.tag_family ?? null
}

/**
 * VERA-790 — resolve the chip family from a PACKAGE's stored `tag_shop_product`. The persisted
 * `selected_variant` is a subset (id/sku/title/image) that does NOT carry `tag_family`, but the full
 * `variants[]` array does — so look the selected variant up there. Returns null (→ encode blocked
 * with "Missing tag type") only when the family is genuinely absent.
 */
export function familyForTagShopProduct(
  tsp?:
    | {
        selected_variant?: { id?: string; tag_family?: TagFamily | null } | null
        variants?: any[]
      }
    | null,
): TagFamily | null {
  const sv = tsp?.selected_variant
  const full = tsp?.variants?.find((v: any) => v?.id === sv?.id)
  return (full?.tag_family ?? sv?.tag_family) ?? null
}

/** The resolved target an encode session runs against (a package to fill, or a company for test
 *  tags). Built by `EncodeTagsModal`'s configure step, or by `PackagesPage`'s `packageRowToTarget`. */
export interface EncodeSessionTarget {
  kind: 'package' | 'company'
  id: string
  name: string
  // Null when the variant has no `tag_family` set — the session blocks with "Missing tag type"
  // rather than encoding a guessed family.
  family: TagFamily | null
  // Product-level title ("30mm Sticker Tag") for the package preview — distinct from `productTitle`,
  // which can be the variant tier ("250"). Optional; only the package flows set it.
  productName?: string
  productSku?: string
  productTitle?: string
  productImage?: string
  // Lock is chosen in the session itself (right before the first tap), so this is always `false`
  // here — the session owns the real toggle.
  lock: boolean
  // VERA-790 Part B — the package's target capacity (the selected variant's `quantity`, e.g.
  // "Qty 250"), so the session's counter can show "N / target". Undefined for company/test targets
  // (no package, no capacity) or when the package has no product/variant assigned yet.
  targetCount?: number
}
